import { useId } from 'react'

/**
 * Inline logo components that render SVG directly instead of using the sprite system.
 * This fixes Safari's SVG gradient rendering bug where gradients in sprites show as black/white.
 * See: https://github.com/airbnb/visx/issues/317
 */

export function InlineLogo({ className }: { className?: string }) {
	// Use React's useId to ensure unique gradient IDs across multiple instances
	const gradientId = useId()

	return (
		<svg
			className={className}
			viewBox="0 0 508.776164327779497 69.368179602720375"
			xmlns="http://www.w3.org/2000/svg"
		>
			<defs>
				<linearGradient
					id={`logo-grad-1-${gradientId}`}
					x1="25.365"
					y1="53.828"
					x2="240.112"
					y2="-33.136"
					gradientUnits="userSpaceOnUse"
				>
					<stop offset="0" stopColor="#f6921e" />
					<stop offset="1" stopColor="#eb008b" />
				</linearGradient>
				<linearGradient
					id={`logo-grad-2-${gradientId}`}
					x1="34.496"
					y1="76.374"
					x2="249.243"
					y2="-10.589"
					gradientUnits="userSpaceOnUse"
				>
					<stop offset="0" stopColor="#f6921e" />
					<stop offset="1" stopColor="#eb008b" />
				</linearGradient>
				<linearGradient
					id={`logo-grad-3-${gradientId}`}
					x1="37.557"
					y1="83.933"
					x2="252.304"
					y2="-3.031"
					gradientUnits="userSpaceOnUse"
				>
					<stop offset="0" stopColor="#f6921e" />
					<stop offset="1" stopColor="#eb008b" />
				</linearGradient>
				<linearGradient
					id={`logo-grad-4-${gradientId}`}
					x1="39.14"
					y1="87.843"
					x2="253.887"
					y2="0.879"
					gradientUnits="userSpaceOnUse"
				>
					<stop offset="0" stopColor="#f6921e" />
					<stop offset="1" stopColor="#eb008b" />
				</linearGradient>
				<linearGradient
					id={`logo-grad-5-${gradientId}`}
					x1="43.43"
					y1="98.437"
					x2="258.177"
					y2="11.473"
					gradientUnits="userSpaceOnUse"
				>
					<stop offset="0" stopColor="#f6921e" />
					<stop offset="1" stopColor="#eb008b" />
				</linearGradient>
				<linearGradient
					id={`logo-grad-6-${gradientId}`}
					x1="49.514"
					y1="113.459"
					x2="264.26"
					y2="26.495"
					gradientUnits="userSpaceOnUse"
				>
					<stop offset="0" stopColor="#f6921e" />
					<stop offset="1" stopColor="#eb008b" />
				</linearGradient>
				<linearGradient
					id={`logo-grad-7-${gradientId}`}
					x1="0"
					y1="26.973"
					x2="52.528"
					y2="26.973"
					gradientUnits="userSpaceOnUse"
				>
					<stop offset="0" stopColor="#f6921e" />
					<stop offset="1" stopColor="#eb008b" />
				</linearGradient>
			</defs>
			<g>
				{/* "identity" text - dark color */}
				<path
					d="M285.974,8.136c-1.19,0-2.177-.392-2.962-1.177-.785-.785-1.177-1.772-1.177-2.962,0-1.189.392-2.152,1.177-2.89.785-.737,1.772-1.106,2.962-1.106,1.142,0,2.117.369,2.926,1.106.808.738,1.213,1.701,1.213,2.89,0,1.19-.405,2.177-1.213,2.962-.81.785-1.784,1.177-2.926,1.177zM282.976,52.811V15.272h5.995v37.539h-5.995z"
					fill="#231a1a"
				/>
				<path
					d="M311.523,53.667c-3.331,0-6.28-.856-8.85-2.569-2.569-1.713-4.567-4.056-5.994-7.03-1.428-2.972-2.141-6.339-2.141-10.098,0-3.806.714-7.172,2.141-10.098,1.427-2.926,3.425-5.234,5.994-6.923,2.57-1.688,5.542-2.533,8.921-2.533,2.95,0,5.483.595,7.6,1.784,2.117,1.19,3.794,2.855,5.032,4.996V1.427h5.994v51.384h-5.424l-.499-5.995c-.714,1.19-1.654,2.308-2.819,3.354-1.166,1.047-2.57,1.892-4.211,2.534-1.641.642-3.557.963-5.744.963zm.785-5.209c2.378,0,4.448-.582,6.209-1.749,1.76-1.165,3.115-2.83,4.068-4.995.951-2.165,1.427-4.722,1.427-7.672,0-2.95-.476-5.507-1.427-7.672-.952-2.165-2.308-3.83-4.068-4.996-1.761-1.165-3.831-1.748-6.209-1.748-2.237,0-4.235.583-5.995,1.748-1.76,1.166-3.14,2.831-4.139,4.996-.999,2.165-1.499,4.722-1.499,7.672,0,2.95.5,5.507,1.499,7.672.999,2.165,2.379,3.83,4.139,4.995,1.76,1.167,3.758,1.749,5.995,1.749z"
					fill="#231a1a"
				/>
				<path
					d="M352.344,53.667c-3.331,0-6.28-.81-8.849-2.426-2.57-1.617-4.58-3.9-6.031-6.851-1.452-2.949-2.176-6.398-2.176-10.348,0-3.949.724-7.398,2.176-10.348,1.451-2.95,3.473-5.233,6.066-6.851,2.593-1.617,5.578-2.426,8.957-2.426,3.615,0,6.637.809,9.063,2.426,2.427,1.618,4.258,3.782,5.496,6.494,1.236,2.712,1.855,5.686,1.855,8.921v1.427c0,.477-.024,1.048-.071,1.713h-29.046v-4.639h23.265c-.096-3.663-1.154-6.459-3.176-8.385-2.022-1.927-4.531-2.89-7.529-2.89-1.998,0-3.843.488-5.531,1.462-1.689.976-3.058,2.416-4.104,4.318-1.047,1.903-1.57,4.282-1.57,7.137v1.998c0,3.14.523,5.77,1.57,7.886,1.046,2.117,2.414,3.7,4.104,4.746,1.688,1.047,3.532,1.57,5.531,1.57,2.664,0,4.757-.511,6.28-1.535,1.522-1.022,2.64-2.485,3.354-4.389h5.923c-.571,2.094-1.559,3.973-2.962,5.638-1.404,1.666-3.164,2.974-5.281,3.925-2.117.951-4.556,1.427-7.315,1.427z"
					fill="#231a1a"
				/>
				<path
					d="M373.61,52.811V15.272h5.424l.285,5.923c1.094-2.14,2.677-3.806,4.746-4.995,2.07-1.189,4.461-1.784,7.173-1.784,2.758,0,5.161.524,7.207,1.57,2.046,1.047,3.64,2.64,4.782,4.782,1.142,2.14,1.713,4.853,1.713,8.135v23.908h-5.995v-23.265c0-3.33-.821-5.828-2.462-7.494-1.642-1.665-3.843-2.498-6.602-2.498-1.855,0-3.568.441-5.138,1.321-1.57.88-2.819,2.176-3.747,3.889-.928,1.713-1.391,3.807-1.391,6.281v21.766h-5.995z"
					fill="#231a1a"
				/>
				<path
					d="M425.85,52.811c-2.141,0-3.997-.332-5.567-.999-1.57-.665-2.772-1.795-3.604-3.39-.833-1.594-1.249-3.746-1.249-6.459v-21.624h-6.566v-5.067h6.566l.785-9.349h5.21v9.349h10.633v5.067h-10.633v21.624c0,2.237.451,3.747,1.356,4.532.903.785,2.498,1.178,4.781,1.178h4.354v5.138h-6.066z"
					fill="#231a1a"
				/>
				<path
					d="M440.265,8.136c-1.19,0-2.177-.392-2.962-1.177-.785-.785-1.177-1.772-1.177-2.962,0-1.189.392-2.152,1.177-2.89.785-.737,1.772-1.106,2.962-1.106,1.142,0,2.116.369,2.926,1.106.808.738,1.213,1.701,1.213,2.89,0,1.19-.405,2.177-1.213,2.962-.81.785-1.784,1.177-2.926,1.177zM437.268,52.811V15.272h5.994v37.539h-5.994z"
					fill="#231a1a"
				/>
				<path
					d="M464.886,52.811c-2.141,0-3.996-.332-5.566-.999-1.571-.665-2.773-1.795-3.604-3.39-.833-1.594-1.249-3.746-1.249-6.459v-21.624h-6.566v-5.067h6.566l.785-9.349h5.209v9.349h10.634v5.067h-10.634v21.624c0,2.237.452,3.747,1.356,4.532.903.785,2.498,1.178,4.782,1.178h4.353v5.138h-6.066z"
					fill="#231a1a"
				/>
				<path
					d="M479.944,68.512l8.493-19.698h-2.069l-14.06-33.542h6.495l11.632,28.904,12.133-28.904h6.208l-22.623,53.24h-6.209z"
					fill="#231a1a"
				/>
			</g>
			{/* "ignite" text with gradient */}
			<g>
				<path
					d="M89.878,12.703c-1.903,0-3.45-.546-4.639-1.641-1.19-1.094-1.784-2.474-1.784-4.14,0-1.712.594-3.115,1.784-4.21,1.189-1.094,2.736-1.642,4.639-1.642,1.95,0,3.532.548,4.746,1.642,1.213,1.095,1.82,2.498,1.82,4.21,0,1.666-.607,3.045-1.82,4.14-1.214,1.095-2.796,1.641-4.746,1.641zM84.526,52.811V16.843h10.705v35.968H84.526z"
					fill={`url(#logo-grad-1-${gradientId})`}
				/>
				<path
					d="M116.926,69.368c-3.522,0-6.566-.417-9.135-1.249-2.569-.833-4.556-2.082-5.959-3.747-1.404-1.666-2.105-3.759-2.105-6.28,0-1.809.487-3.497,1.463-5.067.974-1.57,2.366-2.962,4.175-4.175,1.808-1.213,3.972-2.223,6.494-3.033l3.569,5.424c-2.095.619-3.652,1.427-4.675,2.426-1.024.999-1.534,2.117-1.534,3.354,0,.952.332,1.76.999,2.427.666.665,1.581,1.165,2.748,1.498,1.165.333,2.462.5,3.889.5,1.38,0,2.581-.168,3.604-.5,1.023-.333,1.831-.821,2.427-1.463.594-.642.892-1.416.892-2.319,0-1.19-.405-2.141-1.213-2.855-.81-.713-2.475-1.166-4.996-1.356-2.331-.189-4.437-.464-6.316-.821-1.88-.356-3.545-.796-4.995-1.32-1.452-.523-2.701-1.094-3.747-1.713-1.047-.618-1.856-1.237-2.427-1.856v-1.641l7.85-8.636,7.351,2.284-9.492,9.634,3.283-5.424c.475.286.975.56,1.499.821.523.262,1.165.488,1.927.678.76.191,1.713.369,2.855.535,1.141.167,2.616.346,4.424.535,3.093.286,5.649.869,7.672,1.749,2.022.881,3.521,2.082,4.496,3.604.975,1.522,1.463,3.378,1.463,5.566,0,2.141-.595,4.163-1.784,6.066-1.19,1.903-2.998,3.437-5.424,4.603-2.427,1.166-5.52,1.749-9.278,1.749zm.071-26.976c-2.997,0-5.591-.594-7.779-1.785-2.189-1.188-3.878-2.771-5.067-4.745-1.19-1.974-1.784-4.198-1.784-6.673,0-2.426.594-4.626,1.784-6.601,1.189-1.974,2.878-3.568,5.067-4.782,2.188-1.213,4.782-1.82,7.779-1.82,3.044,0,5.661.607,7.85,1.82,2.188,1.214,3.854,2.808,4.996,4.782,1.142,1.975,1.713,4.175,1.713,6.601,0,2.475-.571,4.699-1.713,6.673-1.142,1.974-2.808,3.557-4.996,4.745-2.189,1.191-4.806,1.785-7.85,1.785zm0-8.064c1.617,0,2.95-.429,3.997-1.285,1.046-.857,1.57-2.116,1.57-3.783,0-1.665-.524-2.926-1.57-3.782-1.047-.856-2.38-1.285-3.997-1.285-1.618,0-2.962.429-4.032,1.285-1.07.856-1.606,2.117-1.606,3.782,0,1.667.536,2.926,1.606,3.783,1.07.856,2.414,1.285,4.032,1.285zm5.995-10.134l-1.57-7.35h14.916v6.065l-13.346,1.285z"
					fill={`url(#logo-grad-2-${gradientId})`}
				/>
				<path
					d="M140.833,52.811V16.843h9.349l.856,5.781c1.094-1.999,2.641-3.604,4.639-4.817,1.998-1.213,4.425-1.82,7.28-1.82,2.997,0,5.518.643,7.564,1.927,2.046,1.285,3.604,3.129,4.675,5.531,1.07,2.403,1.606,5.341,1.606,8.814v20.553h-10.634v-19.554c0-2.616-.56-4.65-1.677-6.102-1.118-1.451-2.89-2.177-5.317-2.177-1.427,0-2.724.346-3.889,1.035-1.167.69-2.082,1.677-2.748,2.962-.667,1.284-.999,2.831-.999,4.638v19.198h-10.705z"
					fill={`url(#logo-grad-3-${gradientId})`}
				/>
				<path
					d="M187.65,12.703c-1.904,0-3.45-.546-4.639-1.641-1.19-1.094-1.784-2.474-1.784-4.14,0-1.712.594-3.115,1.784-4.21,1.189-1.094,2.735-1.642,4.639-1.642,1.95,0,3.532.548,4.746,1.642,1.213,1.095,1.82,2.498,1.82,4.21,0,1.666-.607,3.045-1.82,4.14-1.214,1.095-2.796,1.641-4.746,1.641zM182.297,52.811V16.843h10.705v35.968h-10.705z"
					fill={`url(#logo-grad-4-${gradientId})`}
				/>
				<path
					d="M215.625,52.811c-2.569,0-4.853-.416-6.851-1.249-1.998-.832-3.545-2.2-4.639-4.103-1.095-1.903-1.641-4.496-1.641-7.78v-13.916h-6.138v-8.92h6.138l1.142-10.134h9.563v10.134h9.206v8.92h-9.206v14.06c0,1.427.321,2.439.963,3.033.643.595,1.749.892,3.319.892h4.924v9.063h-6.78z"
					fill={`url(#logo-grad-5-${gradientId})`}
				/>
				<path
					d="M243.886,53.667c-3.711,0-6.97-.773-9.777-2.319-2.808-1.546-5.008-3.711-6.601-6.494-1.595-2.783-2.391-5.983-2.391-9.599,0-3.711.785-7.017,2.355-9.92,1.57-2.901,3.758-5.185,6.566-6.851,2.807-1.665,6.066-2.498,9.777-2.498,3.615,0,6.78.785,9.492,2.355,2.712,1.57,4.841,3.7,6.387,6.387,1.546,2.689,2.32,5.793,2.32,9.314,0,.476-.013,1.011-.036,1.605-.024.596-.084,1.202-.178,1.82h-29.046v-6.209h18.341c-.096-1.998-.821-3.604-2.177-4.817-1.356-1.213-3.033-1.82-5.031-1.82-1.523,0-2.903.357-4.14,1.07-1.237.714-2.224,1.785-2.961,3.212-.738,1.427-1.106,3.236-1.106,5.424v2.141c0,1.666.321,3.14.963,4.425.643,1.284,1.57,2.284,2.784,2.997,1.213.714,2.651,1.07,4.317,1.07,1.57,0,2.866-.321,3.89-.963,1.022-.643,1.82-1.463,2.391-2.463h10.919c-.667,2.284-1.809,4.343-3.426,6.174-1.618,1.832-3.593,3.283-5.923,4.353-2.332,1.07-4.901,1.606-7.708,1.606z"
					fill={`url(#logo-grad-6-${gradientId})`}
				/>
			</g>
			{/* Logo icon square */}
			<rect
				x="0"
				y="0.709"
				width="52.528"
				height="52.528"
				rx="2.918"
				ry="2.918"
				fill={`url(#logo-grad-7-${gradientId})`}
			/>
			{/* "ii" letters in white */}
			<g fill="#fff">
				<path d="M20.299,15.896c-1.155,0-2.054-.344-2.697-1.034-.643-.689-.88-1.563-.711-2.621.169-1.058.685-1.931,1.549-2.62.862-.69,1.872-1.035,3.026-1.035,1.186,0,2.101.345,2.744,1.035.643.69.88,1.562.711,2.62-.169,1.058-.685,1.932-1.548,2.621-.864.69-1.889,1.034-3.074,1.034zM12.627,43.45l3.872-24.236h2.839c2.017,0,3.391,1.635,3.069,3.652l-3.288,20.584h-6.492z" />
				<path d="M33.474,15.896c-1.154,0-2.053-.344-2.696-1.034-.644-.689-.88-1.563-.711-2.621.169-1.058.684-1.931,1.548-2.62.863-.69,1.873-1.035,3.026-1.035,1.186,0,2.101.345,2.744,1.035.643.69.881,1.562.712,2.62-.17,1.058-.686,1.932-1.549,2.621-.864.69-1.889,1.034-3.074,1.034zM26.18,41.088l3.117-19.512c.208-1.305,1.435-2.362,2.739-2.362h1.768c1.305,0,2.193,1.058,1.985,2.362l-3.117,19.512c-.209,1.305-1.435,2.362-2.74,2.362h-1.767c-1.305,0-2.194-1.057-1.985-2.362z" />
			</g>
		</svg>
	)
}

export function InlineLogoDarkBg({ className }: { className?: string }) {
	// Use React's useId to ensure unique gradient IDs across multiple instances
	const gradientId = useId()

	return (
		<svg
			className={className}
			viewBox="0 0 508.776164327779497 69.368179602720375"
			xmlns="http://www.w3.org/2000/svg"
		>
			<defs>
				<linearGradient
					id={`logo-dark-grad-1-${gradientId}`}
					x1="25.365"
					y1="53.828"
					x2="240.112"
					y2="-33.136"
					gradientUnits="userSpaceOnUse"
				>
					<stop offset="0" stopColor="#f6921e" />
					<stop offset="1" stopColor="#eb008b" />
				</linearGradient>
				<linearGradient
					id={`logo-dark-grad-2-${gradientId}`}
					x1="34.496"
					y1="76.374"
					x2="249.243"
					y2="-10.589"
					gradientUnits="userSpaceOnUse"
				>
					<stop offset="0" stopColor="#f6921e" />
					<stop offset="1" stopColor="#eb008b" />
				</linearGradient>
				<linearGradient
					id={`logo-dark-grad-3-${gradientId}`}
					x1="37.557"
					y1="83.933"
					x2="252.304"
					y2="-3.031"
					gradientUnits="userSpaceOnUse"
				>
					<stop offset="0" stopColor="#f6921e" />
					<stop offset="1" stopColor="#eb008b" />
				</linearGradient>
				<linearGradient
					id={`logo-dark-grad-4-${gradientId}`}
					x1="39.14"
					y1="87.843"
					x2="253.887"
					y2="0.879"
					gradientUnits="userSpaceOnUse"
				>
					<stop offset="0" stopColor="#f6921e" />
					<stop offset="1" stopColor="#eb008b" />
				</linearGradient>
				<linearGradient
					id={`logo-dark-grad-5-${gradientId}`}
					x1="43.43"
					y1="98.437"
					x2="258.177"
					y2="11.473"
					gradientUnits="userSpaceOnUse"
				>
					<stop offset="0" stopColor="#f6921e" />
					<stop offset="1" stopColor="#eb008b" />
				</linearGradient>
				<linearGradient
					id={`logo-dark-grad-6-${gradientId}`}
					x1="49.514"
					y1="113.459"
					x2="264.26"
					y2="26.495"
					gradientUnits="userSpaceOnUse"
				>
					<stop offset="0" stopColor="#f6921e" />
					<stop offset="1" stopColor="#eb008b" />
				</linearGradient>
				<linearGradient
					id={`logo-dark-grad-7-${gradientId}`}
					x1="0"
					y1="26.973"
					x2="52.528"
					y2="26.973"
					gradientUnits="userSpaceOnUse"
				>
					<stop offset="0" stopColor="#f6921e" />
					<stop offset="1" stopColor="#eb008b" />
				</linearGradient>
			</defs>
			<g>
				{/* "identity" text - white for dark backgrounds */}
				<path
					d="M285.974,8.136c-1.19,0-2.177-.392-2.962-1.177-.785-.785-1.177-1.772-1.177-2.962,0-1.189.392-2.152,1.177-2.89.785-.737,1.772-1.106,2.962-1.106,1.142,0,2.117.369,2.926,1.106.808.738,1.213,1.701,1.213,2.89,0,1.19-.405,2.177-1.213,2.962-.81.785-1.784,1.177-2.926,1.177zM282.976,52.811V15.272h5.995v37.539h-5.995z"
					fill="#fff"
				/>
				<path
					d="M311.523,53.667c-3.331,0-6.28-.856-8.85-2.569-2.569-1.713-4.567-4.056-5.994-7.03-1.428-2.972-2.141-6.339-2.141-10.098,0-3.806.714-7.172,2.141-10.098,1.427-2.926,3.425-5.234,5.994-6.923,2.57-1.688,5.542-2.533,8.921-2.533,2.95,0,5.483.595,7.6,1.784,2.117,1.19,3.794,2.855,5.032,4.996V1.427h5.994v51.384h-5.424l-.499-5.995c-.714,1.19-1.654,2.308-2.819,3.354-1.166,1.047-2.57,1.892-4.211,2.534-1.641.642-3.557.963-5.744.963zm.785-5.209c2.378,0,4.448-.582,6.209-1.749,1.76-1.165,3.115-2.83,4.068-4.995.951-2.165,1.427-4.722,1.427-7.672,0-2.95-.476-5.507-1.427-7.672-.952-2.165-2.308-3.83-4.068-4.996-1.761-1.165-3.831-1.748-6.209-1.748-2.237,0-4.235.583-5.995,1.748-1.76,1.166-3.14,2.831-4.139,4.996-.999,2.165-1.499,4.722-1.499,7.672,0,2.95.5,5.507,1.499,7.672.999,2.165,2.379,3.83,4.139,4.995,1.76,1.167,3.758,1.749,5.995,1.749z"
					fill="#fff"
				/>
				<path
					d="M352.344,53.667c-3.331,0-6.28-.81-8.849-2.426-2.57-1.617-4.58-3.9-6.031-6.851-1.452-2.949-2.176-6.398-2.176-10.348,0-3.949.724-7.398,2.176-10.348,1.451-2.95,3.473-5.233,6.066-6.851,2.593-1.617,5.578-2.426,8.957-2.426,3.615,0,6.637.809,9.063,2.426,2.427,1.618,4.258,3.782,5.496,6.494,1.236,2.712,1.855,5.686,1.855,8.921v1.427c0,.477-.024,1.048-.071,1.713h-29.046v-4.639h23.265c-.096-3.663-1.154-6.459-3.176-8.385-2.022-1.927-4.531-2.89-7.529-2.89-1.998,0-3.843.488-5.531,1.462-1.689.976-3.058,2.416-4.104,4.318-1.047,1.903-1.57,4.282-1.57,7.137v1.998c0,3.14.523,5.77,1.57,7.886,1.046,2.117,2.414,3.7,4.104,4.746,1.688,1.047,3.532,1.57,5.531,1.57,2.664,0,4.757-.511,6.28-1.535,1.522-1.022,2.64-2.485,3.354-4.389h5.923c-.571,2.094-1.559,3.973-2.962,5.638-1.404,1.666-3.164,2.974-5.281,3.925-2.117.951-4.556,1.427-7.315,1.427z"
					fill="#fff"
				/>
				<path
					d="M373.61,52.811V15.272h5.424l.285,5.923c1.094-2.14,2.677-3.806,4.746-4.995,2.07-1.189,4.461-1.784,7.173-1.784,2.758,0,5.161.524,7.207,1.57,2.046,1.047,3.64,2.64,4.782,4.782,1.142,2.14,1.713,4.853,1.713,8.135v23.908h-5.995v-23.265c0-3.33-.821-5.828-2.462-7.494-1.642-1.665-3.843-2.498-6.602-2.498-1.855,0-3.568.441-5.138,1.321-1.57.88-2.819,2.176-3.747,3.889-.928,1.713-1.391,3.807-1.391,6.281v21.766h-5.995z"
					fill="#fff"
				/>
				<path
					d="M425.85,52.811c-2.141,0-3.997-.332-5.567-.999-1.57-.665-2.772-1.795-3.604-3.39-.833-1.594-1.249-3.746-1.249-6.459v-21.624h-6.566v-5.067h6.566l.785-9.349h5.21v9.349h10.633v5.067h-10.633v21.624c0,2.237.451,3.747,1.356,4.532.903.785,2.498,1.178,4.781,1.178h4.354v5.138h-6.066z"
					fill="#fff"
				/>
				<path
					d="M440.265,8.136c-1.19,0-2.177-.392-2.962-1.177-.785-.785-1.177-1.772-1.177-2.962,0-1.189.392-2.152,1.177-2.89.785-.737,1.772-1.106,2.962-1.106,1.142,0,2.116.369,2.926,1.106.808.738,1.213,1.701,1.213,2.89,0,1.19-.405,2.177-1.213,2.962-.81.785-1.784,1.177-2.926,1.177zM437.268,52.811V15.272h5.994v37.539h-5.994z"
					fill="#fff"
				/>
				<path
					d="M464.886,52.811c-2.141,0-3.996-.332-5.566-.999-1.571-.665-2.773-1.795-3.604-3.39-.833-1.594-1.249-3.746-1.249-6.459v-21.624h-6.566v-5.067h6.566l.785-9.349h5.209v9.349h10.634v5.067h-10.634v21.624c0,2.237.452,3.747,1.356,4.532.903.785,2.498,1.178,4.782,1.178h4.353v5.138h-6.066z"
					fill="#fff"
				/>
				<path
					d="M479.944,68.512l8.493-19.698h-2.069l-14.06-33.542h6.495l11.632,28.904,12.133-28.904h6.208l-22.623,53.24h-6.209z"
					fill="#fff"
				/>
			</g>
			{/* "ignite" text with gradient */}
			<g>
				<path
					d="M89.878,12.703c-1.903,0-3.45-.546-4.639-1.641-1.19-1.094-1.784-2.474-1.784-4.14,0-1.712.594-3.115,1.784-4.21,1.189-1.094,2.736-1.642,4.639-1.642,1.95,0,3.532.548,4.746,1.642,1.213,1.095,1.82,2.498,1.82,4.21,0,1.666-.607,3.045-1.82,4.14-1.214,1.095-2.796,1.641-4.746,1.641zM84.526,52.811V16.843h10.705v35.968H84.526z"
					fill={`url(#logo-dark-grad-1-${gradientId})`}
				/>
				<path
					d="M116.926,69.368c-3.522,0-6.566-.417-9.135-1.249-2.569-.833-4.556-2.082-5.959-3.747-1.404-1.666-2.105-3.759-2.105-6.28,0-1.809.487-3.497,1.463-5.067.974-1.57,2.366-2.962,4.175-4.175,1.808-1.213,3.972-2.223,6.494-3.033l3.569,5.424c-2.095.619-3.652,1.427-4.675,2.426-1.024.999-1.534,2.117-1.534,3.354,0,.952.332,1.76.999,2.427.666.665,1.581,1.165,2.748,1.498,1.165.333,2.462.5,3.889.5,1.38,0,2.581-.168,3.604-.5,1.023-.333,1.831-.821,2.427-1.463.594-.642.892-1.416.892-2.319,0-1.19-.405-2.141-1.213-2.855-.81-.713-2.475-1.166-4.996-1.356-2.331-.189-4.437-.464-6.316-.821-1.88-.356-3.545-.796-4.995-1.32-1.452-.523-2.701-1.094-3.747-1.713-1.047-.618-1.856-1.237-2.427-1.856v-1.641l7.85-8.636,7.351,2.284-9.492,9.634,3.283-5.424c.475.286.975.56,1.499.821.523.262,1.165.488,1.927.678.76.191,1.713.369,2.855.535,1.141.167,2.616.346,4.424.535,3.093.286,5.649.869,7.672,1.749,2.022.881,3.521,2.082,4.496,3.604.975,1.522,1.463,3.378,1.463,5.566,0,2.141-.595,4.163-1.784,6.066-1.19,1.903-2.998,3.437-5.424,4.603-2.427,1.166-5.52,1.749-9.278,1.749zm.071-26.976c-2.997,0-5.591-.594-7.779-1.785-2.189-1.188-3.878-2.771-5.067-4.745-1.19-1.974-1.784-4.198-1.784-6.673,0-2.426.594-4.626,1.784-6.601,1.189-1.974,2.878-3.568,5.067-4.782,2.188-1.213,4.782-1.82,7.779-1.82,3.044,0,5.661.607,7.85,1.82,2.188,1.214,3.854,2.808,4.996,4.782,1.142,1.975,1.713,4.175,1.713,6.601,0,2.475-.571,4.699-1.713,6.673-1.142,1.974-2.808,3.557-4.996,4.745-2.189,1.191-4.806,1.785-7.85,1.785zm0-8.064c1.617,0,2.95-.429,3.997-1.285,1.046-.857,1.57-2.116,1.57-3.783,0-1.665-.524-2.926-1.57-3.782-1.047-.856-2.38-1.285-3.997-1.285-1.618,0-2.962.429-4.032,1.285-1.07.856-1.606,2.117-1.606,3.782,0,1.667.536,2.926,1.606,3.783,1.07.856,2.414,1.285,4.032,1.285zm5.995-10.134l-1.57-7.35h14.916v6.065l-13.346,1.285z"
					fill={`url(#logo-dark-grad-2-${gradientId})`}
				/>
				<path
					d="M140.833,52.811V16.843h9.349l.856,5.781c1.094-1.999,2.641-3.604,4.639-4.817,1.998-1.213,4.425-1.82,7.28-1.82,2.997,0,5.518.643,7.564,1.927,2.046,1.285,3.604,3.129,4.675,5.531,1.07,2.403,1.606,5.341,1.606,8.814v20.553h-10.634v-19.554c0-2.616-.56-4.65-1.677-6.102-1.118-1.451-2.89-2.177-5.317-2.177-1.427,0-2.724.346-3.889,1.035-1.167.69-2.082,1.677-2.748,2.962-.667,1.284-.999,2.831-.999,4.638v19.198h-10.705z"
					fill={`url(#logo-dark-grad-3-${gradientId})`}
				/>
				<path
					d="M187.65,12.703c-1.904,0-3.45-.546-4.639-1.641-1.19-1.094-1.784-2.474-1.784-4.14,0-1.712.594-3.115,1.784-4.21,1.189-1.094,2.735-1.642,4.639-1.642,1.95,0,3.532.548,4.746,1.642,1.213,1.095,1.82,2.498,1.82,4.21,0,1.666-.607,3.045-1.82,4.14-1.214,1.095-2.796,1.641-4.746,1.641zM182.297,52.811V16.843h10.705v35.968h-10.705z"
					fill={`url(#logo-dark-grad-4-${gradientId})`}
				/>
				<path
					d="M215.625,52.811c-2.569,0-4.853-.416-6.851-1.249-1.998-.832-3.545-2.2-4.639-4.103-1.095-1.903-1.641-4.496-1.641-7.78v-13.916h-6.138v-8.92h6.138l1.142-10.134h9.563v10.134h9.206v8.92h-9.206v14.06c0,1.427.321,2.439.963,3.033.643.595,1.749.892,3.319.892h4.924v9.063h-6.78z"
					fill={`url(#logo-dark-grad-5-${gradientId})`}
				/>
				<path
					d="M243.886,53.667c-3.711,0-6.97-.773-9.777-2.319-2.808-1.546-5.008-3.711-6.601-6.494-1.595-2.783-2.391-5.983-2.391-9.599,0-3.711.785-7.017,2.355-9.92,1.57-2.901,3.758-5.185,6.566-6.851,2.807-1.665,6.066-2.498,9.777-2.498,3.615,0,6.78.785,9.492,2.355,2.712,1.57,4.841,3.7,6.387,6.387,1.546,2.689,2.32,5.793,2.32,9.314,0,.476-.013,1.011-.036,1.605-.024.596-.084,1.202-.178,1.82h-29.046v-6.209h18.341c-.096-1.998-.821-3.604-2.177-4.817-1.356-1.213-3.033-1.82-5.031-1.82-1.523,0-2.903.357-4.14,1.07-1.237.714-2.224,1.785-2.961,3.212-.738,1.427-1.106,3.236-1.106,5.424v2.141c0,1.666.321,3.14.963,4.425.643,1.284,1.57,2.284,2.784,2.997,1.213.714,2.651,1.07,4.317,1.07,1.57,0,2.866-.321,3.89-.963,1.022-.643,1.82-1.463,2.391-2.463h10.919c-.667,2.284-1.809,4.343-3.426,6.174-1.618,1.832-3.593,3.283-5.923,4.353-2.332,1.07-4.901,1.606-7.708,1.606z"
					fill={`url(#logo-dark-grad-6-${gradientId})`}
				/>
			</g>
			{/* Logo icon square */}
			<rect
				x="0"
				y="0.709"
				width="52.528"
				height="52.528"
				rx="2.918"
				ry="2.918"
				fill={`url(#logo-dark-grad-7-${gradientId})`}
			/>
			{/* "ii" letters in white */}
			<g fill="#fff">
				<path d="M20.299,15.896c-1.155,0-2.054-.344-2.697-1.034-.643-.689-.88-1.563-.711-2.621.169-1.058.685-1.931,1.549-2.62.862-.69,1.872-1.035,3.026-1.035,1.186,0,2.101.345,2.744,1.035.643.69.88,1.562.711,2.62-.169,1.058-.685,1.932-1.548,2.621-.864.69-1.889,1.034-3.074,1.034zM12.627,43.45l3.872-24.236h2.839c2.017,0,3.391,1.635,3.069,3.652l-3.288,20.584h-6.492z" />
				<path d="M33.474,15.896c-1.154,0-2.053-.344-2.696-1.034-.644-.689-.88-1.563-.711-2.621.169-1.058.684-1.931,1.548-2.62.863-.69,1.873-1.035,3.026-1.035,1.186,0,2.101.345,2.744,1.035.643.69.881,1.562.712,2.62-.17,1.058-.686,1.932-1.549,2.621-.864.69-1.889,1.034-3.074,1.034zM26.18,41.088l3.117-19.512c.208-1.305,1.435-2.362,2.739-2.362h1.768c1.305,0,2.193,1.058,1.985,2.362l-3.117,19.512c-.209,1.305-1.435,2.362-2.74,2.362h-1.767c-1.305,0-2.194-1.057-1.985-2.362z" />
			</g>
		</svg>
	)
}
